import * as React from 'react';
import moment from 'moment';
import { connectWithRouter } from 'helpers/connectWithRouter';
import { bindActionCreators, Dispatch } from 'redux';
import { hasPermission } from 'helpers/Permission';
import { getOrgExpenseReports, getMoreOrgExpenseReports, emptyOrgExpenseReports, OrgExpenseReportsParams } from 'actions/ExpenseReports/Get';
import { editExpenseReport } from 'actions/ExpenseReports/Edit';
import RecordsTable, { Column, BulkAction } from './RecordsTable';
import EditRecordModal from './EditRecordModal';
import SyncStatusFilterBar from './SyncStatusFilterBar';
import { ReduxState } from 'reducers/types';
import { PermissionTypes } from '../../constants';
import { QUERY_LIMIT_25 } from '../../configs/General';
import CircularProgress from '@mui/material/CircularProgress';
import { ExpenseReport, Cursor, SYNC_STATUS_OPTIONS } from './expenseTypes';

const styles = require('./recordStyles.pcss');

const COLUMNS: Column[] = [
  {
    label: 'Report',
    render: (r: ExpenseReport) => r.title ?? '',
  },
  {
    label: 'Reimbursement Date',
    render: (r: ExpenseReport) => {
      const raw = r.scheduledToReimburseDate?.[r.userId ?? ''] ?? '';
      return moment(raw).isValid() ? moment(raw).format('MMM DD, YYYY') : '';
    },
  },
  {
    label: 'Amount',
    render: (r: ExpenseReport) => `$${(r.amounts?.totalSpent ?? 0).toFixed(2)}`,
  },
  {
    label: 'Status',
    render: (r: ExpenseReport) => r.status ?? '',
  },
  {
    label: 'Sync Status',
    render: (r: ExpenseReport) => r.syncToGlStatus ?? '',
  },
];

type Props = {
  organizationId: string;
  expenseReports: ExpenseReport[];
  isLoading: boolean;
  isLoadingMore: boolean;
  hasMore: boolean;
  cursor: Cursor | null;
  success: boolean | null;
  getOrgExpenseReports: (organizationId: string, params?: OrgExpenseReportsParams) => void;
  getMoreOrgExpenseReports: (organizationId: string, params: OrgExpenseReportsParams, cursor: Cursor) => void;
  editExpenseReport: (params: { reportIds: string[]; syncToGlStatus: string }) => void;
  emptyOrgExpenseReports: () => void;
};

type State = {
  editModalOpen: boolean;
  selectedReport: ExpenseReport | null;
  filterSyncStatus: string;
  filterDateFrom: string;
  filterDateTo: string;
};

class OrgExpenseReportsTable extends React.Component<Props, State> {
  private sentinelRef = React.createRef<HTMLDivElement>();
  private observer: IntersectionObserver | null = null;

  constructor(props: Props) {
    super(props);

    this.state = {
      editModalOpen: false,
      selectedReport: null,
      filterSyncStatus: '',
      filterDateFrom: '',
      filterDateTo: '',
    };
  }

  componentDidMount() {
    if (this.props.organizationId) {
      this.fetchReports();
    }
    this.setupObserver();
  }

  componentDidUpdate(prevProps: Props) {
    if (prevProps.success !== this.props.success && this.props.success === true) {
      this.fetchReports();
      this.setState({ editModalOpen: false, selectedReport: null });
    }

    if (prevProps.hasMore !== this.props.hasMore || prevProps.isLoadingMore !== this.props.isLoadingMore) {
      this.setupObserver();
    }
  }

  componentWillUnmount() {
    if (this.observer) {
      this.observer.disconnect();
    }
    this.props.emptyOrgExpenseReports();
  }

  setupObserver = () => {
    if (this.observer) {
      this.observer.disconnect();
    }

    const sentinel = this.sentinelRef.current;
    if (!sentinel || !this.props.hasMore || this.props.isLoadingMore) return;

    this.observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          this.loadMore();
        }
      },
      { threshold: 0.1 },
    );

    this.observer.observe(sentinel);
  };

  getFilterParams = (): OrgExpenseReportsParams => {
    const params: OrgExpenseReportsParams = { limit: QUERY_LIMIT_25 };
    if (this.state.filterSyncStatus) {
      params.syncToGlStatus = this.state.filterSyncStatus;
    }
    if (this.state.filterDateFrom) {
      params.dateFrom = this.state.filterDateFrom;
    }
    if (this.state.filterDateTo) {
      params.dateTo = this.state.filterDateTo;
    }
    return params;
  };

  fetchReports = () => {
    this.props.getOrgExpenseReports(this.props.organizationId, this.getFilterParams());
  };

  loadMore = () => {
    if (this.props.cursor && this.props.hasMore && !this.props.isLoadingMore) {
      this.props.getMoreOrgExpenseReports(
        this.props.organizationId,
        this.getFilterParams(),
        this.props.cursor,
      );
    }
  };

  handleFilterApply = () => {
    this.fetchReports();
  };

  handleFilterReset = () => {
    this.setState(
      { filterSyncStatus: '', filterDateFrom: '', filterDateTo: '' },
      () => this.fetchReports(),
    );
  };

  onClickRow = (report: ExpenseReport) => {
    if (hasPermission('expenseReport', PermissionTypes.update)) {
      this.setState({ editModalOpen: true, selectedReport: report });
    }
  };

  onCloseModal = () => {
    this.setState({ editModalOpen: false, selectedReport: null });
  };

  onSubmitEdit = (values: Record<string, string>) => {
    const { selectedReport } = this.state;
    if (selectedReport?.id) {
      this.props.editExpenseReport({
        reportIds: [selectedReport.id],
        syncToGlStatus: values.syncToGlStatus,
      });
    }
  };

  getDisplayFields = () => {
    const r = this.state.selectedReport;
    return [
      { label: 'Report', value: r?.title ?? '' },
      { label: 'Status', value: r?.status ?? '' },
      {
        label: 'Amount',
        value: `$${(r?.amounts?.totalSpent ?? 0).toFixed(2)}`,
      },
    ];
  };

  getEditFields = () => [
    {
      label: 'Sync to GL Status',
      key: 'syncToGlStatus',
      options: SYNC_STATUS_OPTIONS,
    },
  ];

  getInitialValues = () => ({
    syncToGlStatus: this.state.selectedReport?.syncToGlStatus ?? '',
  });

  onBulkUpdateSyncStatus = (selectedIds: string[], status: string) => {
    this.props.editExpenseReport({
      reportIds: selectedIds,
      syncToGlStatus: status,
    });
  };

  getBulkActions = (): BulkAction[] => {
    return SYNC_STATUS_OPTIONS.map((opt) => ({
      label: `Set ${opt.label}`,
      onClick: (ids: string[]) => this.onBulkUpdateSyncStatus(ids, opt.value),
    }));
  };

  render() {
    const canEdit = hasPermission('expenseReport', PermissionTypes.update);

    return (
      <>
        <SyncStatusFilterBar
          labelId="report-sync-filter-label"
          filterSyncStatus={this.state.filterSyncStatus}
          filterDateFrom={this.state.filterDateFrom}
          filterDateTo={this.state.filterDateTo}
          onSyncStatusChange={(value) => this.setState({ filterSyncStatus: value })}
          onDateFromChange={(value) => this.setState({ filterDateFrom: value })}
          onDateToChange={(value) => this.setState({ filterDateTo: value })}
          onApply={this.handleFilterApply}
          onReset={this.handleFilterReset}
        />

        <RecordsTable
          columns={COLUMNS}
          records={this.props.expenseReports}
          tabs={[]}
          isLoading={this.props.isLoading}
          onClickRow={canEdit ? this.onClickRow : undefined}
          keyField="id"
          selectable={canEdit}
          bulkActions={canEdit ? this.getBulkActions() : undefined}
        />

        {this.props.hasMore && (
          <div ref={this.sentinelRef} className={styles.sentinelLoader}>
            {this.props.isLoadingMore && <CircularProgress size={24} />}
          </div>
        )}

        <EditRecordModal
          open={this.state.editModalOpen}
          title="Edit Expense Report"
          displayFields={this.getDisplayFields()}
          editFields={this.getEditFields()}
          initialValues={this.getInitialValues()}
          onClose={this.onCloseModal}
          onSubmit={this.onSubmitEdit}
        />
      </>
    );
  }
}

function mapStateToProps(state: ReduxState) {
  return {
    expenseReports: state.orgExpenseReports.expenseReports,
    isLoading: state.orgExpenseReports.isLoading,
    isLoadingMore: state.orgExpenseReports.isLoadingMore,
    hasMore: state.orgExpenseReports.hasMore,
    cursor: state.orgExpenseReports.cursor,
    success: state.orgExpenseReports.success,
  };
}

function mapDispatchToProps(dispatch: Dispatch) {
  return bindActionCreators(
    {
      getOrgExpenseReports,
      getMoreOrgExpenseReports,
      editExpenseReport,
      emptyOrgExpenseReports,
    },
    dispatch,
  );
}

export default connectWithRouter(
  mapStateToProps,
  mapDispatchToProps,
)(OrgExpenseReportsTable);
